import metricMetadataIcon from '../assets/metric-metadata-icon-blue.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata, indexMetricIds } from "../utils/dashboardUtils";
import { useDashboardColor } from '../context/DashboardColorContext';

interface DisaggregateMetricCardProps {
    id: number;
    title: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const DisaggregateMetricCard = ({ id, title, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: DisaggregateMetricCardProps) => {
    const color = useDashboardColor();

    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Index metric?
    const isIndexMetric = indexMetricIds.includes(id);

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id ?? 'main'}`;
    const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
            // Return ongoing request if already in-flight
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    // Prefetch on mount (throttled sequentially)
    useEffect(() => {
        // Only fetch if not already cached or loaded
        if (!metadata) {
            fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id))
            .then(res => res?.data && setMetadata(res.data))
            .catch(console.error);
        }

        if (!distributionData) {
            const distKey = `distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`;
            fetchWithCache(distKey, () => fetchIndicatorBinCount(id, geoDetails, geoTag))
            .then(res => res && setDistributionData(res))
            .catch(console.error);
        }

        if (!countyDistributionData) {
            const countyKey = `countyDist-${id}-53033`;
            fetchWithCache(countyKey, () => fetchDistributionData(id, [], null))
            .then(res => res && setCountyDistributionData(res))
            .catch(console.error);
        }
    }, [id, geoDetails, geoTag]);

    const handleClick = async () => {
        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
            setShowMetadata((prev) => !prev);
            setLoading(true);

            try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                metadata
                ? Promise.resolve({ data: metadata })
                : fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)),

                distributionData
                ? Promise.resolve(distributionData)
                : fetchWithCache(`distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`, () =>
                    fetchIndicatorBinCount(id, geoDetails, geoTag)
                    ),

                countyDistributionData
                ? Promise.resolve(countyDistributionData)
                : fetchWithCache(`countyDist-${id}-53033`, () =>
                    fetchDistributionData(id, [], null)
                    ),
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

            } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`));
            } finally {
            setLoading(false);
            }
        }, 150);
    };

    return (
        <div className={`rounded-t-md bg-white`}>
            <div className="flex items-start justify-between px-2">
                <div className='space-y-0.5 mb-2'>
                    <h3 className="text-sm font-semibold text-port-text">{title}</h3>
                    {isIndexMetric &&
                        <div className="rounded-lg h-4 w-10 px-1.5 text-[10px] text-white text-center" style={{ backgroundColor: color }}>Index</div> 
                    }
                </div>
                {showMetadataIcon && 
                    <button
                        type="button"
                        onClick={handleClick}
                        aria-haspopup="dialog"
                        aria-expanded={showMetadata ? 'true' : 'false'}
                        aria-controls={modalId}
                        aria-label={`Additional information about ${title}`}
                        className="
                        inline-flex h-6 w-6 items-center justify-center rounded
                        transition-transform hover:scale-105 focus:scale-105
                        focus-visible:outline-none
                        focus-visible:ring-2 focus-visible:ring-offset-2
                        focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                        dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                        "
                    >
                        <img src={metricMetadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
                    </button>
                }
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                disaggData={disaggData}
                stateData={null}
                nationalData={null}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};


export default DisaggregateMetricCard;
